import { getTripDisplayStops } from './tripDisplayStops';

const hasWaypointActivity = (waypoint = {}) => (
  Number(waypoint.pickup_count) > 0
  || Number(waypoint.dropoff_count) > 0
  || Number(waypoint.pickup_riders) > 0
  || Number(waypoint.dropoff_riders) > 0
);

const getWaypointKey = (waypointId) => String(waypointId);

const normalizeWaypointId = (waypointId) => {
  const numericWaypointId = Number(waypointId);
  return Number.isFinite(numericWaypointId) ? numericWaypointId : null;
};

const getRowSortValue = (row = {}) => {
  const effectiveSequence = Number(row.effective_sequence);
  if (Number.isFinite(effectiveSequence)) {
    return effectiveSequence;
  }

  const sequenceNumber = Number(row.sequence_number);
  return Number.isFinite(sequenceNumber) ? sequenceNumber : Number.MAX_SAFE_INTEGER;
};

const sortRowsByDisplayOrder = (rows = []) => (
  [...rows].sort((left, right) => {
    const sortDiff = getRowSortValue(left) - getRowSortValue(right);
    if (sortDiff !== 0) {
      return sortDiff;
    }

    return (Number(left.sequence_number) || 0) - (Number(right.sequence_number) || 0);
  })
);

const buildRowLookup = (rows = []) => new Map(
  rows
    .map((row) => [getWaypointKey(row?.waypoint_id), row])
    .filter(([waypointId]) => waypointId !== 'undefined' && waypointId !== 'null')
);

export const hasCompletedHistoricalEvidence = ({ actualStop = {}, stopData = {} } = {}) => (
  Boolean(actualStop?.actual_arrival_time)
  || Boolean(stopData?.is_manual_skip)
  || Boolean(stopData?.is_skipped)
  || Boolean(actualStop?.is_skipped)
  || hasWaypointActivity(stopData)
  || hasWaypointActivity(actualStop)
);

const buildCompletedComparisonRow = ({
  waypointId,
  stopData = {},
  actualStop = {},
  estimatedStop = {},
  websocketStop = {},
  pickupStopId,
  dropoffStopId
}) => {
  const sequenceNumber = stopData.sequence_number
    ?? actualStop.sequence_number
    ?? estimatedStop.sequence_number
    ?? 0;
  const effectiveSequence = stopData.effective_sequence
    ?? estimatedStop.effective_sequence
    ?? actualStop.effective_sequence
    ?? sequenceNumber;

  return {
    waypoint_id: waypointId,
    waypoint_name: stopData.waypoint_name
      || actualStop.waypoint_name
      || estimatedStop.waypoint_name
      || websocketStop.waypoint_name,
    sequence_number: sequenceNumber,
    effective_sequence: effectiveSequence,
    point_type: stopData.point_type
      || actualStop.point_type
      || estimatedStop.point_type
      || 'stop',
    is_departure: Boolean(stopData.is_departure)
      || Boolean(actualStop.is_departure)
      || Number(sequenceNumber) === 0,
    can_pickup: stopData.can_pickup ?? actualStop.can_pickup ?? false,
    can_dropoff: stopData.can_dropoff ?? actualStop.can_dropoff ?? false,
    address: stopData.address ?? actualStop.address ?? null,
    latitude: stopData.latitude ?? actualStop.latitude ?? null,
    longitude: stopData.longitude ?? actualStop.longitude ?? null,
    pickup_riders: stopData.pickup_riders ?? 0,
    dropoff_riders: stopData.dropoff_riders ?? 0,
    pickup_luggages: stopData.pickup_luggages ?? 0,
    dropoff_luggages: stopData.dropoff_luggages ?? 0,
    is_user_pickup: Boolean(stopData.is_user_pickup) || waypointId === pickupStopId,
    is_user_dropoff: Boolean(stopData.is_user_dropoff) || waypointId === dropoffStopId,
    boarding_location_info: stopData.boarding_location_info || actualStop.boarding_location_info || null,
    boarding_location_url: stopData.boarding_location_url || actualStop.boarding_location_url || null,
    boarding_location_image: stopData.boarding_location_image || actualStop.boarding_location_image || null,
    is_skipped: Boolean(stopData.is_manual_skip || stopData.is_skipped || actualStop.is_skipped),
    is_manual_skip: Boolean(stopData.is_manual_skip),
    actual_source: actualStop,
    estimated_source: estimatedStop,
    websocket_source: websocketStop
  };
};

export const buildCompletedComparisonDisplayRows = ({
  actualWaypoints = [],
  estimatedWaypoints = [],
  websocketWaypoints = [],
  tripData,
  tripPlan = null,
  pickupStopId = null,
  dropoffStopId = null
}) => {
  const displayStops = getTripDisplayStops(tripData);
  const rawStops = Array.isArray(tripData?.stops) ? tripData.stops : [];
  const bypassedWaypointIds = new Set((tripPlan?.bypassed_waypoint_ids || []).map(Number));

  const displayStopById = buildRowLookup(displayStops);
  const rawStopById = buildRowLookup(rawStops);
  const actualById = buildRowLookup(actualWaypoints);
  const estimatedById = buildRowLookup(estimatedWaypoints);
  const websocketById = buildRowLookup(websocketWaypoints);
  const rows = [];
  const seenWaypointIds = new Set();

  for (const displayStop of displayStops) {
    const waypointId = normalizeWaypointId(displayStop?.waypoint_id);
    if (!Number.isFinite(waypointId)) {
      continue;
    }

    const actualStop = actualById.get(getWaypointKey(waypointId)) || {};
    const historical = hasCompletedHistoricalEvidence({
      actualStop,
      stopData: displayStop
    });

    if (bypassedWaypointIds.has(waypointId) && !historical) {
      continue;
    }

    rows.push(buildCompletedComparisonRow({
      waypointId,
      stopData: displayStop,
      actualStop,
      estimatedStop: estimatedById.get(getWaypointKey(waypointId)) || {},
      websocketStop: websocketById.get(getWaypointKey(waypointId)) || {},
      pickupStopId,
      dropoffStopId
    }));
    seenWaypointIds.add(getWaypointKey(waypointId));
  }

  for (const actualStop of actualWaypoints) {
    const waypointId = normalizeWaypointId(actualStop?.waypoint_id);
    const waypointKey = getWaypointKey(waypointId);
    if (!Number.isFinite(waypointId) || seenWaypointIds.has(waypointKey)) {
      continue;
    }

    const stopData = displayStopById.get(waypointKey) || rawStopById.get(waypointKey) || {};
    if (!hasCompletedHistoricalEvidence({ actualStop, stopData })) {
      continue;
    }

    rows.push(buildCompletedComparisonRow({
      waypointId,
      stopData,
      actualStop,
      estimatedStop: estimatedById.get(waypointKey) || {},
      websocketStop: websocketById.get(waypointKey) || {},
      pickupStopId,
      dropoffStopId
    }));
  }

  return sortRowsByDisplayOrder(rows);
};

export default buildCompletedComparisonDisplayRows;
